import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const NAV_LINKS = [
  { href: '/', icon: 'ri-home-line', label: 'Home' },
  { href: '/olympics-props', icon: 'ri-medal-line', label: 'Olympics' },
  { href: '/super-bowl-props', icon: 'ri-football-line', label: 'Super Bowl' },
  { href: '/markets', icon: 'ri-bar-chart-2-line', label: 'Markets' },
  { href: '/liquidity-edge', icon: 'ri-drop-line', label: 'Why L2L' },
  { href: '/my-account', icon: 'ri-user-line', label: 'My Account' },
];

const STATS = [
  { value: 98, suffix: '%', label: 'Payout Rate', sub: 'vs ~91% at sportsbooks', icon: 'ri-percent-line', color: 'text-teal-400', bg: 'bg-teal-500/10', border: 'border-teal-500/30' },
  { value: 2, suffix: '%', label: 'Max Trading Fee', sub: 'vs 10%+ vig elsewhere', icon: 'ri-price-tag-3-line', color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' },
  { value: 50, suffix: '+', label: 'States Available', sub: 'CFTC federal license', icon: 'ri-map-pin-line', color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30' },
  { value: 100, suffix: 'x', label: 'Market Growth', sub: 'Prediction markets vs legacy books', icon: 'ri-line-chart-line', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
];

const ADVANTAGES = [
  {
    icon: 'ri-exchange-funds-line',
    color: 'text-teal-400',
    bg: 'bg-teal-500/10',
    border: 'border-teal-500/20',
    title: 'Peer-to-Peer Liquidity',
    desc: 'Every trade is matched between users — not against the house. This means prices are set by real market demand, not a bookmaker\'s margin. Deep liquidity pools ensure tight spreads and instant execution.',
    stat: '0% house edge',
    statColor: 'text-teal-400',
  },
  {
    icon: 'ri-shield-check-line',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/20',
    title: 'CFTC Regulated Exchange',
    desc: 'L2L operates under a federal CFTC license — the same regulatory framework as the Chicago Mercantile Exchange. Your funds are held in segregated, insured accounts. No state-by-state patchwork.',
    stat: 'Federal protection',
    statColor: 'text-emerald-400',
  },
  {
    icon: 'ri-swap-line',
    color: 'text-cyan-400',
    bg: 'bg-cyan-500/10',
    border: 'border-cyan-500/20',
    title: 'Trade In & Out Anytime',
    desc: 'Unlike locked-in sportsbook bets, L2L positions can be bought and sold at any time before settlement. Lock in profits early, cut losses, or ride momentum — just like trading stocks.',
    stat: 'Full flexibility',
    statColor: 'text-cyan-400',
  },
  {
    icon: 'ri-eye-line',
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/20',
    title: 'Transparent Price Discovery',
    desc: 'All prices are visible, market-driven, and reflect true crowd probability. No hidden vig buried in confusing American odds. See exactly what you\'re paying and what you stand to win.',
    stat: 'Real-time pricing',
    statColor: 'text-amber-400',
  },
  {
    icon: 'ri-trophy-line',
    color: 'text-rose-400',
    bg: 'bg-rose-500/10',
    border: 'border-rose-500/20',
    title: 'Winners Are Welcome',
    desc: 'Traditional sportsbooks limit or ban winning players. On L2L, sharp bettors improve market efficiency and provide liquidity for others. The more you win, the more valuable you are to the ecosystem.',
    stat: 'No account limits',
    statColor: 'text-rose-400',
  },
  {
    icon: 'ri-file-text-line',
    color: 'text-lime-400',
    bg: 'bg-lime-500/10',
    border: 'border-lime-500/20',
    title: 'Capital Gains Tax Treatment',
    desc: 'Profits on L2L are classified as capital gains (1099), not gambling income (W-2G). Losses can offset ordinary income up to $3,000/year — a massive structural advantage over traditional sportsbooks.',
    stat: '1099 tax treatment',
    statColor: 'text-lime-400',
  },
];

const VIG_COMPARISON = [
  { label: 'L2L Exchange', vig: 2, color: 'bg-teal-400', textColor: 'text-teal-400' },
  { label: 'Kalshi', vig: 3, color: 'bg-emerald-400', textColor: 'text-emerald-400' },
  { label: 'Polymarket', vig: 4, color: 'bg-cyan-400', textColor: 'text-cyan-400' },
  { label: 'DraftKings', vig: 10, color: 'bg-amber-400', textColor: 'text-amber-400' },
  { label: 'FanDuel', vig: 10, color: 'bg-orange-400', textColor: 'text-orange-400' },
  { label: 'BetMGM', vig: 12, color: 'bg-rose-400', textColor: 'text-rose-400' },
  { label: 'Caesars', vig: 14, color: 'bg-red-500', textColor: 'text-red-400' },
];

const MARKET_SHARE_DATA = [
  { year: '2022', legacy: 88, prediction: 12 },
  { year: '2023', legacy: 78, prediction: 22 },
  { year: '2024', legacy: 62, prediction: 38 },
  { year: '2025E', legacy: 45, prediction: 55 },
  { year: '2026E', legacy: 28, prediction: 72 },
];

const LIQUIDITY_FLOW = [
  { icon: 'ri-user-line', label: 'Buyer', color: 'text-teal-400', bg: 'bg-teal-500/10', border: 'border-teal-500/30' },
  { icon: 'ri-arrow-right-line', label: '', color: 'text-slate-500', bg: '', border: '' },
  { icon: 'ri-building-line', label: 'L2L Exchange', color: 'text-white', bg: 'bg-slate-700/60', border: 'border-slate-500/40' },
  { icon: 'ri-arrow-right-line', label: '', color: 'text-slate-500', bg: '', border: '' },
  { icon: 'ri-user-line', label: 'Seller', color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' },
];

function useCountUp(target: number, duration = 1800, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

function AnimatedStat({ value, suffix, label, sub, icon, color, bg, border, animate }: typeof STATS[0] & { animate: boolean }) {
  const count = useCountUp(value, 1600, animate);
  return (
    <div className={`rounded-xl border ${border} p-6 flex flex-col items-center text-center transition-all hover:shadow-xl`} style={{ background: 'rgba(30,30,30,0.85)' }}>
      <div className={`w-12 h-12 ${bg} rounded-xl flex items-center justify-center mb-3`}>
        <i className={`${icon} ${color} text-2xl`}></i>
      </div>
      <div className={`text-4xl font-extrabold ${color} mb-1`}>{animate ? count : 0}{suffix}</div>
      <div className="text-white font-semibold text-base mb-1">{label}</div>
      <div className="text-slate-500 text-xs">{sub}</div>
    </div>
  );
}

export default function LiquidityEdgePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [marketsDropdownOpen, setMarketsDropdownOpen] = useState(false);
  const [mobileMarketsOpen, setMobileMarketsOpen] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);
  const marketsDropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (marketsDropdownRef.current && !marketsDropdownRef.current.contains(e.target as Node)) {
        setMarketsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schemaId = 'schema-liquidity-edge';
    const existing = document.getElementById(schemaId);
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = schemaId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'The Liquidity Edge \u2014 L2L Sports Trading Exchange',
      description: 'Discover L2L\'s exchange model advantages: peer-to-peer liquidity, CFTC regulation, near-zero fees, and transparent pricing.',
      url: `${siteUrl}/liquidity-edge`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Liquidity Edge', item: `${siteUrl}/liquidity-edge` },
        ],
      },
    });
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  const handleMobileLinkClick = () => {
    setMobileMenuOpen(false);
    setMobileMarketsOpen(false);
  };

  const maxVig = Math.max(...VIG_COMPARISON.map(v => v.vig));

  return (
    <div className="min-h-screen" style={{ background: '#2b2b2b' }}>
      {/* Demo Banner */}
      <div className="bg-amber-500 text-slate-900 px-4 py-2 z-[100] shadow-md">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-center">
          <i className="ri-alert-line text-lg"></i>
          <span className="font-bold text-sm md:text-base tracking-wide uppercase">
            Demo Only — Experience the Future of Sports Betting — From Sportsbook to Prediction Market
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-lg border-b border-slate-700/50" style={{ background: 'rgba(43,43,43,0.97)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 gap-4">
            <a className="flex items-center gap-2 flex-shrink-0" href="/">
              <div className="h-11 w-44 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/356d7ca1b6cdb5f780c70d0d0bd7c02d.png"
                  alt="RTJ Logo"
                  className="h-full w-full object-contain"
                />
              </div>
            </a>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center justify-center gap-1 flex-1">
              <a href="/"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"><i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>Home</div></a>
              <a href="/olympics-props"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"><i className="ri-medal-line w-4 h-4 flex items-center justify-center"></i>Olympics</div></a>
              <a href="/super-bowl-props"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"><i className="ri-football-line w-4 h-4 flex items-center justify-center"></i>Super Bowl</div></a>

              {/* Markets Dropdown */}
              <div className="relative" ref={marketsDropdownRef}>
                <button onClick={() => setMarketsDropdownOpen(!marketsDropdownOpen)} className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white">
                  <i className="ri-bar-chart-2-line w-4 h-4 flex items-center justify-center"></i>
                  Markets
                  <i className={`ri-arrow-down-s-line text-xs transition-transform ${marketsDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {marketsDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-52 rounded-xl border border-slate-700/80 shadow-2xl overflow-hidden z-50" style={{ background: 'rgba(30,30,30,0.98)' }}>
                    <div className="px-3 py-2 border-b border-slate-700/50">
                      <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Market Types</span>
                    </div>
                    {[
                      { label: 'Spreads', icon: 'ri-bar-chart-grouped-line', color: 'text-teal-400', path: '/markets/spreads' },
                      { label: 'Money Lines', icon: 'ri-copper-coin-line', color: 'text-amber-400', path: '/markets/money-lines' },
                      { label: 'Futures', icon: 'ri-calendar-event-line', color: 'text-emerald-400', path: '/markets/futures' },
                      { label: 'Parlays', icon: 'ri-stack-line', color: 'text-violet-400', path: '/markets/parlays' },
                      { label: 'Player Props', icon: 'ri-user-star-line', color: 'text-cyan-400', path: '/markets/player-props' },
                    ].map(item => (
                      <a key={item.path} href={item.path} onClick={() => setMarketsDropdownOpen(false)}>
                        <div className="flex items-center gap-3 px-4 py-2.5 text-slate-300 hover:bg-slate-800/60 hover:text-white transition-colors cursor-pointer">
                          <i className={`${item.icon} ${item.color} text-base w-4 h-4 flex items-center justify-center`}></i>
                          <span className="text-sm font-medium">{item.label}</span>
                        </div>
                      </a>
                    ))}
                    <div className="border-t border-slate-700/50 px-3 py-2">
                      <a href="/markets" onClick={() => setMarketsDropdownOpen(false)}>
                        <div className="flex items-center justify-between text-teal-400 hover:text-teal-300 transition-colors cursor-pointer text-sm font-semibold py-1">
                          <span>All Markets</span>
                          <i className="ri-arrow-right-line"></i>
                        </div>
                      </a>
                    </div>
                  </div>
                )}
              </div>

              <a href="/liquidity-edge">
                <div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 bg-teal-500/20 text-teal-400">
                  <i className="ri-drop-line w-4 h-4 flex items-center justify-center"></i>
                  Why L2L
                </div>
              </a>
              <a href="/my-account"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"><i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>My Account</div></a>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="hidden sm:flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-teal-500/20 to-teal-600/10 rounded-lg border border-teal-500/30">
                <i className="ri-wallet-3-line text-teal-400 text-sm"></i>
                <span className="font-semibold text-white text-sm whitespace-nowrap">$10,000</span>
              </div>
              <button className="relative p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg border border-slate-700 transition-colors cursor-pointer">
                <i className="ri-file-list-3-line text-teal-500 text-lg w-5 h-5 flex items-center justify-center"></i>
              </button>
              <a href="/my-account">
                <div className="p-2 rounded-lg transition-colors cursor-pointer bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700">
                  <i className="ri-user-line text-lg w-5 h-5 flex items-center justify-center"></i>
                </div>
              </a>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors cursor-pointer" aria-label="Toggle menu">
                <i className={`text-xl transition-transform duration-200 ${mobileMenuOpen ? 'ri-close-line rotate-90' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${mobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`} style={{ background: 'rgba(30,30,30,0.98)' }}>
          <div className="px-4 py-4 border-t border-slate-700/50">
            <div className="space-y-1">
              {NAV_LINKS.filter(l => l.label !== 'Markets').map(link => (
                <a key={link.href} href={link.href} onClick={handleMobileLinkClick}>
                  <div className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-all cursor-pointer ${link.href === '/liquidity-edge' ? 'bg-teal-500/20 text-teal-400' : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'}`}>
                    <i className={`${link.icon} w-5 h-5 flex items-center justify-center`}></i>
                    {link.label}
                  </div>
                </a>
              ))}
              <div>
                <button onClick={() => setMobileMarketsOpen(!mobileMarketsOpen)} className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg text-base font-medium transition-all cursor-pointer text-slate-300 hover:bg-slate-800/50 hover:text-white">
                  <div className="flex items-center gap-3"><i className="ri-bar-chart-2-line w-5 h-5 flex items-center justify-center"></i>Markets</div>
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileMarketsOpen ? 'rotate-180' : ''}`}></i>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${mobileMarketsOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="ml-4 mt-1 space-y-1 border-l-2 border-slate-700/50 pl-4">
                    {[
                      { label: 'All Markets', icon: 'ri-bar-chart-2-line', color: 'text-teal-400', path: '/markets' },
                      { label: 'Spreads', icon: 'ri-bar-chart-grouped-line', color: 'text-teal-400', path: '/markets/spreads' },
                      { label: 'Money Lines', icon: 'ri-copper-coin-line', color: 'text-amber-400', path: '/markets/money-lines' },
                      { label: 'Futures', icon: 'ri-calendar-event-line', color: 'text-emerald-400', path: '/markets/futures' },
                      { label: 'Parlays', icon: 'ri-stack-line', color: 'text-violet-400', path: '/markets/parlays' },
                      { label: 'Player Props', icon: 'ri-user-star-line', color: 'text-cyan-400', path: '/markets/player-props' },
                    ].map(item => (
                      <a key={item.path} href={item.path} onClick={handleMobileLinkClick}>
                        <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer text-slate-300 hover:bg-slate-800/50 hover:text-white">
                          <i className={`${item.icon} ${item.color} w-4 h-4 flex items-center justify-center`}></i>
                          {item.label}
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://public.readdy.ai/ai/img_res/3724a57e42fb655735a1860a9853ba47.jpg')" }}></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/60 to-[#2b2b2b]"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="flex items-center gap-1.5 px-4 py-1.5 bg-teal-500/20 border border-teal-500/40 rounded-full text-teal-400 text-sm font-semibold">
              <span className="w-2 h-2 bg-teal-400 rounded-full animate-pulse"></span>
              L2L Exchange Model
            </span>
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-5 leading-tight">
            The <span className="bg-gradient-to-r from-teal-400 via-emerald-400 to-cyan-400 bg-clip-text text-transparent">Liquidity Edge</span>
          </h1>
          <p className="text-slate-300 text-lg md:text-xl max-w-3xl mx-auto mb-8 leading-relaxed">
            L2L's exchange model eliminates the house edge entirely. Peer-to-peer liquidity, transparent pricing, and CFTC regulation give traders a structural advantage that traditional sportsbooks simply cannot match.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-300 mb-10">
            {[
              { icon: 'ri-shield-check-line', label: 'CFTC Regulated', color: 'text-teal-400' },
              { icon: 'ri-exchange-line', label: 'Peer-to-Peer', color: 'text-emerald-400' },
              { icon: 'ri-percent-line', label: 'Near-Zero Fees', color: 'text-cyan-400' },
              { icon: 'ri-trophy-line', label: 'Winners Welcome', color: 'text-amber-400' },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-2">
                <i className={`${f.icon} ${f.color} text-lg`}></i>
                <span>{f.label}</span>
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate('/markets')} className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-gradient-to-r from-teal-400 to-emerald-400 hover:from-teal-500 hover:to-emerald-500 text-slate-900 font-bold rounded-lg shadow-lg shadow-teal-500/25 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-rocket-line"></i>
              Start Trading
            </button>
            <button onClick={() => document.getElementById('vig-chart')?.scrollIntoView({ behavior: 'smooth' })} className="inline-flex items-center justify-center gap-2 px-8 py-3.5 border border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white rounded-lg transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-bar-chart-2-line"></i>
              See the Data
            </button>
          </div>
        </div>
      </section>

      {/* Animated Stats */}
      <section ref={statsRef} className="py-14 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {STATS.map((stat, i) => (
              <AnimatedStat key={i} {...stat} animate={statsVisible} />
            ))}
          </div>
        </div>
      </section>

      {/* How the Exchange Works */}
      <section className="py-14 px-4" style={{ background: 'rgba(20,20,20,0.5)' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">How the Exchange Works</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">Unlike a sportsbook where you bet against the house, L2L matches buyers and sellers directly — creating a true financial exchange for sports outcomes.</p>
          </div>

          {/* Flow Diagram */}
          <div className="rounded-2xl border border-slate-700/60 p-8 mb-10" style={{ background: 'rgba(30,30,30,0.85)' }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              {/* Sportsbook Model */}
              <div className="rounded-xl border border-rose-500/30 p-6" style={{ background: 'rgba(239,68,68,0.04)' }}>
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-8 h-8 bg-rose-500/20 rounded-lg flex items-center justify-center">
                    <i className="ri-close-circle-line text-rose-400"></i>
                  </div>
                  <h3 className="text-white font-bold text-lg">Traditional Sportsbook</h3>
                </div>
                <div className="flex items-center justify-center gap-3 mb-5">
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-14 h-14 bg-slate-700/60 border border-slate-600 rounded-full flex items-center justify-center">
                      <i className="ri-user-line text-slate-300 text-xl"></i>
                    </div>
                    <span className="text-xs text-slate-400">You</span>
                  </div>
                  <div className="flex flex-col items-center gap-1 flex-1">
                    <div className="w-full h-px bg-rose-500/40 relative">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="bg-rose-500/20 text-rose-400 text-xs px-2 py-0.5 rounded-full border border-rose-500/30 whitespace-nowrap">10% vig taken</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-14 h-14 bg-rose-500/20 border border-rose-500/30 rounded-full flex items-center justify-center">
                      <i className="ri-building-4-line text-rose-400 text-xl"></i>
                    </div>
                    <span className="text-xs text-rose-400">The House</span>
                  </div>
                </div>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li className="flex items-center gap-2"><i className="ri-close-line text-rose-400"></i>House sets all prices with hidden margin</li>
                  <li className="flex items-center gap-2"><i className="ri-close-line text-rose-400"></i>Winners get limited or banned</li>
                  <li className="flex items-center gap-2"><i className="ri-close-line text-rose-400"></i>Locked in until event ends</li>
                  <li className="flex items-center gap-2"><i className="ri-close-line text-rose-400"></i>State-by-state restrictions</li>
                </ul>
              </div>

              {/* L2L Model */}
              <div className="rounded-xl border border-teal-500/30 p-6" style={{ background: 'rgba(20,184,166,0.04)' }}>
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-8 h-8 bg-teal-500/20 rounded-lg flex items-center justify-center">
                    <i className="ri-check-double-line text-teal-400"></i>
                  </div>
                  <h3 className="text-white font-bold text-lg">L2L Exchange Model</h3>
                </div>
                <div className="flex items-center justify-center gap-2 mb-5">
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-14 h-14 bg-teal-500/10 border border-teal-500/30 rounded-full flex items-center justify-center">
                      <i className="ri-user-line text-teal-400 text-xl"></i>
                    </div>
                    <span className="text-xs text-teal-400">Buyer</span>
                  </div>
                  <div className="flex flex-col items-center gap-1 flex-1">
                    <div className="w-full flex flex-col items-center gap-1">
                      <div className="w-full h-px bg-teal-500/40"></div>
                      <div className="w-10 h-10 bg-slate-700/80 border border-slate-600 rounded-lg flex items-center justify-center">
                        <i className="ri-building-line text-slate-300 text-sm"></i>
                      </div>
                      <span className="text-xs text-slate-500">L2L (&lt;2% fee)</span>
                      <div className="w-full h-px bg-emerald-500/40"></div>
                    </div>
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-14 h-14 bg-emerald-500/10 border border-emerald-500/30 rounded-full flex items-center justify-center">
                      <i className="ri-user-line text-emerald-400 text-xl"></i>
                    </div>
                    <span className="text-xs text-emerald-400">Seller</span>
                  </div>
                </div>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-teal-400"></i>
                    Market-driven prices, no hidden vig
                    <span className="ml-1 px-2 py-0.5 bg-teal-500/15 border border-teal-500/30 rounded-full text-teal-400 text-xs font-bold whitespace-nowrap">$1 pays $X</span>
                  </li>
                  <li className="flex items-center gap-2"><i className="ri-check-line text-teal-400"></i>Winners improve market efficiency</li>
                  <li className="flex items-center gap-2"><i className="ri-check-line text-teal-400"></i>Buy &amp; sell positions anytime</li>
                  <li className="flex items-center gap-2"><i className="ri-check-line text-teal-400"></i>CFTC federal license, all 50 states</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vig Comparison Chart */}
      <section id="vig-chart" className="py-14 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">The Vig Advantage</h2>
            <p className="text-slate-400 max-w-xl mx-auto">Every percentage point of vig is money taken from your pocket. L2L's exchange model keeps fees near zero — the lowest in the industry.</p>
          </div>
          <div className="rounded-2xl border border-slate-700/60 p-8" style={{ background: 'rgba(30,30,30,0.85)' }}>
            <div className="mb-4 flex items-center justify-between">
              <span className="text-slate-400 text-sm font-semibold uppercase tracking-wider">Platform Effective Vig / Fee</span>
              <span className="text-xs text-slate-500">Lower is better</span>
            </div>
            <div className="space-y-4">
              {VIG_COMPARISON.map((item, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="w-32 text-sm font-medium text-slate-300 text-right flex-shrink-0">{item.label}</div>
                  <div className="flex-1 relative h-9 bg-slate-800/60 rounded-lg overflow-hidden border border-slate-700/40">
                    <div
                      className={`h-full ${item.color} rounded-lg transition-all duration-1000 flex items-center justify-end pr-3`}
                      style={{ width: `${(item.vig / maxVig) * 100}%`, opacity: 0.85 }}
                    >
                    </div>
                    <div className="absolute inset-0 flex items-center pl-3">
                      <span className={`text-sm font-bold ${i === 0 ? 'text-slate-900' : 'text-white'}`}>{item.vig}%</span>
                    </div>
                  </div>
                  {i === 0 && (
                    <span className="text-xs font-bold text-teal-400 whitespace-nowrap flex-shrink-0">Best ✓</span>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-6 pt-5 border-t border-slate-700/50 flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-slate-500 text-xs">* Vig estimates based on standard -110 lines and typical parlay margins. L2L fee applies to winning trades only.</p>
              <div className="flex items-center gap-2 px-4 py-2 bg-teal-500/10 border border-teal-500/30 rounded-lg">
                <i className="ri-arrow-down-line text-teal-400"></i>
                <span className="text-teal-400 text-sm font-semibold">L2L saves you up to 12% per trade</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Market Share Shift */}
      <section className="py-14 px-4" style={{ background: 'rgba(20,20,20,0.5)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">The Market Is Shifting</h2>
            <p className="text-slate-400 max-w-xl mx-auto">Liquidity is rapidly migrating from legacy sportsbooks to prediction market exchanges. This is the biggest structural shift in sports betting since legalization.</p>
          </div>
          <div className="rounded-2xl border border-slate-700/60 p-8" style={{ background: 'rgba(30,30,30,0.85)' }}>
            <div className="mb-6 flex items-center gap-6 justify-center">
              <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-rose-400"></div><span className="text-sm text-slate-400">Legacy Sportsbooks</span></div>
              <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-teal-400"></div><span className="text-sm text-slate-400">Prediction Markets</span></div>
            </div>
            <div className="space-y-4">
              {MARKET_SHARE_DATA.map((row, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="w-14 text-sm font-semibold text-slate-300 flex-shrink-0">{row.year}</div>
                  <div className="flex-1 flex h-10 rounded-lg overflow-hidden border border-slate-700/40">
                    <div
                      className="bg-rose-500/70 flex items-center justify-center transition-all duration-700"
                      style={{ width: `${row.legacy}%` }}
                    >
                      <span className="text-xs font-bold text-white">{row.legacy}%</span>
                    </div>
                    <div
                      className="bg-teal-500/70 flex items-center justify-center transition-all duration-700"
                      style={{ width: `${row.prediction}%` }}
                    >
                      <span className="text-xs font-bold text-white">{row.prediction}%</span>
                    </div>
                  </div>
                  {i >= 3 && <span className="text-xs text-amber-400 font-semibold whitespace-nowrap flex-shrink-0">Projected</span>}
                </div>
              ))}
            </div>
            <div className="mt-6 pt-5 border-t border-slate-700/50 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
              <div className="p-3 rounded-lg bg-slate-800/50">
                <div className="text-2xl font-extrabold text-rose-400 mb-1">-50%</div>
                <div className="text-xs text-slate-400">Legacy book valuations (24 months)</div>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/50">
                <div className="text-2xl font-extrabold text-teal-400 mb-1">100x</div>
                <div className="text-xs text-slate-400">Kalshi growth (same period)</div>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/50">
                <div className="text-2xl font-extrabold text-amber-400 mb-1">2026</div>
                <div className="text-xs text-slate-400">Projected prediction market majority</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6 Advantages Grid */}
      <section className="py-14 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">6 Structural Advantages</h2>
            <p className="text-slate-400 max-w-xl mx-auto">L2L's exchange model isn't just better pricing — it's a fundamentally different and superior structure for sports traders.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ADVANTAGES.map((adv, i) => (
              <div key={i} className={`rounded-xl border ${adv.border} p-6 hover:shadow-xl transition-all group`} style={{ background: 'rgba(30,30,30,0.85)' }}>
                <div className={`w-12 h-12 ${adv.bg} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <i className={`${adv.icon} ${adv.color} text-2xl`}></i>
                </div>
                <h3 className="text-white font-bold text-lg mb-2">{adv.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-4">{adv.desc}</p>
                <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${adv.bg} border ${adv.border}`}>
                  <i className="ri-check-line"></i>
                  <span className={adv.statColor}>{adv.stat}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* $1 Share Model Explainer */}
      <section className="py-14 px-4" style={{ background: 'rgba(20,20,20,0.5)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">The $1 Share Model</h2>
            <p className="text-slate-400 max-w-xl mx-auto">Every market resolves to $1.00 per winning share. Simple, transparent, and mathematically fair.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: 'ri-shopping-cart-line',
                color: 'text-teal-400',
                bg: 'bg-teal-500/10',
                title: 'Buy at Market Price',
                example: 'Seattle -4.5 at 52¢',
                desc: 'You pay $0.52 per share. The price reflects the crowd\'s probability estimate — 52% chance Seattle covers.',
                highlight: 'Pay: $0.52/share',
                highlightColor: 'text-teal-400',
              },
              {
                icon: 'ri-trophy-line',
                color: 'text-emerald-400',
                bg: 'bg-emerald-500/10',
                title: 'Win at $1.00',
                example: 'Seattle covers — you win',
                desc: 'Each winning share pays exactly $1.00 at settlement. No complicated odds math. No hidden deductions.',
                highlight: 'Payout: $1.00/share',
                highlightColor: 'text-emerald-400',
              },
              {
                icon: 'ri-swap-line',
                color: 'text-cyan-400',
                bg: 'bg-cyan-500/10',
                title: 'Or Trade Early',
                example: 'Seattle up 14 at halftime',
                desc: 'Your shares are now worth 78¢. Sell now for a 50% profit, or hold for the full $1.00 payout.',
                highlight: 'Sell: $0.78/share (+50%)',
                highlightColor: 'text-cyan-400',
              },
            ].map((item, i) => (
              <div key={i} className="rounded-xl border border-slate-700/60 p-6 text-center" style={{ background: 'rgba(30,30,30,0.85)' }}>
                <div className={`w-14 h-14 ${item.bg} rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <i className={`${item.icon} ${item.color} text-2xl`}></i>
                </div>
                <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-1">Step {i + 1}</div>
                <h3 className="text-white font-bold text-lg mb-1">{item.title}</h3>
                <div className="text-xs text-slate-500 italic mb-3">{item.example}</div>
                <p className="text-slate-400 text-sm leading-relaxed mb-4">{item.desc}</p>
                <div className={`text-base font-extrabold ${item.highlightColor}`}>{item.highlight}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="relative rounded-2xl border border-teal-500/20 overflow-hidden p-12 text-center" style={{ background: 'linear-gradient(135deg, rgba(20,184,166,0.10) 0%, rgba(30,30,30,0.97) 60%, rgba(16,185,129,0.08) 100%)' }}>
            <div className="absolute top-0 left-0 w-64 h-64 bg-teal-500/5 rounded-full -translate-x-1/2 -translate-y-1/2 pointer-events-none"></div>
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full translate-x-1/2 translate-y-1/2 pointer-events-none"></div>
            <div className="relative z-10">
              <div className="w-16 h-16 bg-teal-500/15 border border-teal-500/30 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-drop-line text-teal-400 text-3xl"></i>
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">Ready to Trade with the Edge?</h2>
              <p className="text-slate-400 text-lg mb-8 max-w-xl mx-auto">
                Join the exchange revolution. Better prices, full transparency, and no house working against you.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button onClick={() => navigate('/markets')} className="inline-flex items-center justify-center gap-2 px-10 py-4 bg-gradient-to-r from-teal-400 to-emerald-400 hover:from-teal-500 hover:to-emerald-500 text-slate-900 font-bold text-lg rounded-lg shadow-lg shadow-teal-500/25 transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-rocket-line"></i>
                  Explore Markets
                </button>
                <button onClick={() => navigate('/my-account')} className="inline-flex items-center justify-center gap-2 px-10 py-4 border border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white text-lg rounded-lg transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-user-add-line"></i>
                  Create Account
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-700/50 py-8 px-4" style={{ background: '#1e1e1e' }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="h-10 w-40 flex items-center justify-center">
            <img
              src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/356d7ca1b6cdb5f780c70d0d0bd7c02d.png"
              alt="RTJ Logo"
              className="h-full w-full object-contain"
            />
          </div>
          <p className="text-slate-600 text-xs text-center">Demo only. Not real money. For educational purposes only. Must be 18+ to participate.</p>
          <div className="flex items-center gap-4 text-slate-500 text-xs">
            <a href="/" className="hover:text-slate-300 transition-colors cursor-pointer">Home</a>
            <a href="/markets" className="hover:text-slate-300 transition-colors cursor-pointer">Markets</a>
            <a href="/super-bowl-props" className="hover:text-slate-300 transition-colors cursor-pointer">Super Bowl</a>
            <a href="/olympics-props" className="hover:text-slate-300 transition-colors cursor-pointer">Olympics</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
